import type { ResolvedPrice, Service } from '@salonomy/types';

/**
 * WS11 — what price the NEW-APPOINTMENT surface may honestly show before commitment.
 *
 * Pure logic (like features/diary/layout.ts and onboarding/goLive.ts) so the honesty rule is unit-
 * tested directly, not through React. The rule is "shown == charged": a booking surface must never
 * advertise a concrete figure the booking won't be committed at.
 *
 * The create contract now carries the chosen variantId (CreateAppointment.variantId, committed
 * 04ebc45). When the modal sends it, the server RESOLVES the concrete (service, variant?, stylist)
 * price via the pricing engine and FREEZES it as priceQuoted — so the resolved figure the modal
 * shows is exactly what the till charges. This closes the WS9 honesty gap: the surface that earlier
 * had to hide resolved.pricePence behind "from £X · final price set at checkout" (because the frozen
 * contract only committed service.priceFrom) can now anchor the headline on the RESOLVED price.
 *
 * So when a concrete price has resolved we show resolved.pricePence as the committed, exact figure
 * (shown == charged) and report it as NOT indicative. The indicative "from £X" floor (anchored on
 * service.priceFrom) survives only while no concrete price is resolvable yet — e.g. a variant-priced
 * service whose resolve hasn't returned — so the surface still has an honest floor to show. We never
 * do client-side money math: the resolved figure is the server's verbatim pricePence.
 *
 * Returns the duration to show (preferring the resolved variant's chair time, which the booking span
 * also honours via endAt) and the concrete committed price when one has resolved.
 */
export interface BookingPriceDisplay {
  /**
   * The pence figure the booking will actually be committed at. When a concrete price has resolved,
   * this is resolved.pricePence (the server freezes it as priceQuoted once variantId is sent); until
   * then it falls back to service.priceFrom shown as a "from" floor.
   */
  committedPricePence: number;
  /** Chair-time minutes to show — the resolved variant's duration when known, else the service's. */
  durationMin: number;
  /**
   * True only while no concrete price has resolved yet — the displayed figure is then the "from £X"
   * floor (service.priceFrom), not the exact charge. Once resolved, shown == charged and this is false.
   */
  indicative: boolean;
}

/**
 * Resolve the figures the new-appointment surface may show for a service, given the optional resolved
 * price. When a concrete price has resolved we anchor the headline on resolved.pricePence (shown ==
 * charged, since the create path now sends variantId and the server freezes that price); otherwise we
 * fall back to the service.priceFrom floor shown as an indicative "from £X".
 */
export function bookingPriceDisplay(
  service: Pick<Service, 'priceFrom' | 'durationMin'>,
  resolved: Pick<ResolvedPrice, 'pricePence' | 'durationMin'> | undefined,
  hasVariants: boolean,
): BookingPriceDisplay {
  // A concrete price has resolved → it is exactly what the booking is committed at (shown == charged).
  if (resolved) {
    return {
      committedPricePence: resolved.pricePence,
      durationMin: resolved.durationMin,
      indicative: false,
    };
  }
  // No concrete price yet: show the service's own floor. A variant-priced service has a range, so the
  // floor reads as a "from"; a single-price service shows the exact figure it will charge.
  return {
    committedPricePence: service.priceFrom,
    durationMin: service.durationMin,
    indicative: hasVariants,
  };
}
